import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Clock, Building2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// City data with SEO content - Pan India coverage
const CITY_DATA = {
  "mumbai": {
    name: "Mumbai",
    fullName: "Mumbai, Maharashtra",
    tagline: "Office Furniture Solutions in India's Financial Capital",
    description: "As India's business capital, Mumbai demands workspace solutions that maximize expensive real estate. Ezeefit Office Systems provides modular office furniture designed for space efficiency, durability, and modern aesthetics. We serve corporate offices across the entire Mumbai region with end-to-end furniture solutions.",

    image: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?auto=format&fit=crop&w=1280&q=70"
  },
  "navi-mumbai": {
    name: "Navi Mumbai",
    fullName: "Navi Mumbai, Maharashtra",
    tagline: "Modern Workspace Solutions for the Planned City",
    description: "Navi Mumbai's IT parks and corporate hubs need furniture that matches their modern infrastructure. Ezeefit delivers modular workstations, task seating, and complete office solutions for companies throughout Navi Mumbai.",

    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70"
  },
  "thane": {
    name: "Thane",
    fullName: "Thane, Maharashtra",
    tagline: "Corporate Office Furniture for Thane's Growing Businesses",
    description: "Thane's emergence as a corporate destination has created demand for quality office furniture. Ezeefit provides end-to-end workspace solutions for businesses across Thane, from startups to large enterprises.",

    image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=1280&q=70"
  },
  "pune": {
    name: "Pune",
    fullName: "Pune, Maharashtra",
    tagline: "Office Interiors for India's IT Hub",
    description: "Pune's thriving IT and automotive sectors require workspace furniture that supports innovation and collaboration. Ezeefit has supplied office furniture to tech companies and manufacturing firms across Pune with comprehensive workspace solutions.",

    image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1280&q=70"
  },
  "ahmedabad": {
    name: "Ahmedabad",
    fullName: "Ahmedabad, Gujarat",
    tagline: "Workspace Solutions for Gujarat's Commercial Capital",
    description: "Ahmedabad's diverse business landscape—from textiles to IT—needs versatile office furniture solutions. Ezeefit provides customized workspace furniture for Gujarat's growing corporate sector across the entire Ahmedabad region.",

    image: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1280&q=70"
  },
  "bangalore": {
    name: "Bangalore",
    fullName: "Bangalore, Karnataka",
    tagline: "Office Furniture for India's Silicon Valley",
    description: "As India's tech capital, Bangalore's offices need furniture that fosters innovation and startup culture. Ezeefit provides agile workspace solutions for tech companies and corporates throughout Bangalore.",

    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=1280&q=70"
  },
  "hyderabad": {
    name: "Hyderabad",
    fullName: "Hyderabad, Telangana",
    tagline: "Corporate Office Solutions in the City of Pearls",
    description: "Hyderabad hosts major global companies that require quality office interiors. Ezeefit delivers modular furniture solutions for Hyderabad's corporate sector with Pan-city coverage.",

    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70"
  },
  "chennai": {
    name: "Chennai",
    fullName: "Chennai, Tamil Nadu",
    tagline: "Office Interiors for South India's Auto & IT Hub",
    description: "Chennai's unique blend of traditional businesses and modern IT companies needs furniture partners who understand both. Ezeefit provides comprehensive office solutions for Chennai's diverse corporate landscape.",

    image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=1280&q=70"
  },
  "delhi": {
    name: "Delhi",
    fullName: "Delhi NCR",
    tagline: "Office Furniture Solutions in the National Capital",
    description: "Delhi's corporate offices need furniture that reflects prestige and professionalism. Ezeefit provides premium workspace solutions for businesses across the entire Delhi NCR region.",

    image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1280&q=70"
  },
  "gurugram": {
    name: "Gurugram",
    fullName: "Gurugram, Haryana",
    tagline: "Modern Office Solutions for the Millennium City",
    description: "Gurugram's corporate towers house large enterprises that need professional office furniture. Ezeefit supplies modular workspace furniture to corporate offices across Gurugram.",

    image: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1280&q=70"
  },
  "noida": {
    name: "Noida",
    fullName: "Noida, Uttar Pradesh",
    tagline: "Workspace Furniture for the IT Corridor",
    description: "Noida and Greater Noida's expanding corporate sector needs scalable office furniture solutions. Ezeefit provides modular workstations and complete office interiors across the entire Noida region.",

    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=1280&q=70"
  }
};

export default function CityLandingPage() {
  const { city } = useParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const cityData = CITY_DATA[city];

  useCanonical(cityData ? `https://ezeefitofficesystems.in/locations/${city}` : null);
  useMetaDescription(cityData ? `Office furniture in ${cityData.name} — modular workstations, task chairs, conference tables & turnkey solutions by Ezeefit. ${cityData.tagline}` : null);

  useEffect(() => {
    const fetchCityBlogs = async () => {
      try {
        const response = await axios.get(`${API}/blogs`);
        // Filter blogs related to this city
        const cityName = cityData?.name || "";
        const filtered = response.data.filter(blog => 
          blog.title.toLowerCase().includes(cityName.toLowerCase()) ||
          blog.content?.toLowerCase().includes(cityName.toLowerCase())
        );
        setBlogs(filtered.slice(0, 6));
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    if (cityData) {
      fetchCityBlogs();
    } else {
      setLoading(false);
    }
  }, [city, cityData]);

  if (!cityData) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">City Not Found</h2>
          <p className="text-[#64748B] mb-6">We don't have a dedicated page for this location yet.</p>
          <Button asChild>
            <Link to="/locations">View All Locations</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="city-landing-page">
      <Helmet>
        <meta name="description" content={`Office furniture in ${cityData.name} — modular workstations, task chairs, conference tables & turnkey solutions by Ezeefit. ${cityData.tagline}`} />
        <link rel="canonical" href={`https://ezeefitofficesystems.in/locations/${city}`} />
        <meta property="og:title" content={`Office Furniture in ${cityData.name} | Ezeefit Office Systems`} />
        <meta property="og:url" content={`https://ezeefitofficesystems.in/locations/${city}`} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in/" },
          { "@type": "ListItem", "position": 2, "name": "Locations", "item": "https://ezeefitofficesystems.in/locations" },
          { "@type": "ListItem", "position": 3, "name": cityData.name, "item": `https://ezeefitofficesystems.in/locations/${city}` }
        ]
      }) }} />
      {/* Hero */}
      <section className="relative py-24 bg-[#0F172A] noise-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={cityData.image}
            alt={cityData.name}
            className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              All Locations
            </Link>

            <div className="flex items-center gap-2 text-[#F97316] mb-4">
              <MapPin className="w-5 h-5" />
              <span className="font-mono text-sm uppercase tracking-wider">{cityData.fullName}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Office Furniture Solutions in {cityData.name}
            </h1>
            
            <p className="text-xl text-[#94A3B8] max-w-3xl mb-8">
              {cityData.tagline}
            </p>

            <div className="flex flex-wrap gap-4">
              <Button
                asChild
                className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
              >
                <Link to="/contact">
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-transparent border-white text-white hover:bg-white hover:text-[#0F172A] rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
              >
                <Link to="/case-studies">View Projects</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Verifiable Credentials */}
      <section className="py-12 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-[#F97316]">2014</p>
              <p className="text-[#64748B] mt-1">Established</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-[#F97316]">ISO 9001:2015</p>
              <p className="text-[#64748B] mt-1">Certified Products</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-[#F97316]">Mumbai</p>
              <p className="text-[#64748B] mt-1">Own Manufacturing Facility</p>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                Your Trusted Office Furniture Partner in {cityData.name}
              </h2>
              <p className="text-[#64748B] mb-6 leading-relaxed">
                {cityData.description}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F97316] flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">Local Presence</h4>
                    <p className="text-sm text-[#64748B]">Quick delivery and installation across {cityData.name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F97316] flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">Fast Turnaround</h4>
                    <p className="text-sm text-[#64748B]">On-time delivery aligned with your project deadlines</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F97316] flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">Dedicated Support</h4>
                    <p className="text-sm text-[#64748B]">Local team for installation, service, and after-sales support</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img src={cityData.image}
                alt={`Office furniture in ${cityData.name}`}
                className="w-full h-[400px] object-cover" loading="lazy" decoding="async" />
              <div className="absolute -bottom-6 -left-6 bg-[#0F172A] p-6 text-white">
                <p className="text-3xl font-bold text-[#F97316]">ISO 9001:2015</p>
                <p className="text-sm">Certified Manufacturer</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hidden SEO content - Pan India coverage */}
      <section 
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0
        }}
        aria-hidden="true"
      >
        <div>
          <h2>Office Furniture Solutions in {cityData.name}</h2>
          <p>Ezeefit Office Systems is a corporate office furniture manufacturer and supplier in {cityData.name}. We provide end-to-end workspace solutions including modular workstations, task chairs, executive desks, conference tables, and complete office interiors. With Pan-India presence and dedicated service in {cityData.name}, we deliver quality office furniture for IT companies, corporates, startups, co-working spaces, and manufacturing units. Our {cityData.name} operations cover the entire city and surrounding regions with quick delivery and professional installation services.</p>
          <h3>Why Choose Ezeefit for Office Furniture in {cityData.name}</h3>
          <ul>
            <li>Complete office furniture solutions in {cityData.name}</li>
            <li>Modular workstations manufacturer in {cityData.name}</li>
            <li>Corporate office interiors in {cityData.name}</li>
            <li>Task chairs and executive seating supplier in {cityData.name}</li>
            <li>Pan-India delivery with local {cityData.name} service team</li>
          </ul>
        </div>
      </section>

      {/* Related Blogs */}
      {blogs.length > 0 && (
        <section className="py-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-end justify-between mb-12"
            >
              <div>
                <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                  Resources
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]">
                  Office Planning Guides for {cityData.name}
                </h2>
              </div>
              <Link
                to="/blog"
                className="hidden md:inline-flex items-center gap-2 text-[#F97316] font-semibold hover:gap-3 transition-all"
              >
                View All Articles
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, index) => (
                <motion.article
                  key={blog.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-[#E2E8F0] group"
                >
                  <Link to={`/blog/${blog.slug}`}>
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" decoding="async" />
                    </div>
                    <div className="p-6">
                      <span className="text-xs text-[#F97316] font-mono uppercase">{blog.category}</span>
                      <h3 className="text-lg font-bold text-[#0F172A] mt-2 group-hover:text-[#F97316] transition-colors line-clamp-2">
                        {blog.title}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-[#64748B] mt-3">
                        <Clock className="w-4 h-4" />
                        {blog.read_time}
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your {cityData.name} Office?
          </h2>
          <p className="text-lg text-[#94A3B8] mb-8">
            Get a free consultation and quote for your office furniture requirements.
            Our local team in {cityData.name} is ready to help.
          </p>
          <Button
            asChild
            className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-10 py-6 font-semibold uppercase tracking-wider"
          >
            <Link to="/contact">
              Request Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
